import { Link } from "react-router-dom";
import { BookingFlowSidebar, BookingFlowSplit, formatInr } from "./BookingFlowLayout";
import { patientHasValidCarePassForShift, resolveCanonicalSearchShift } from "../../config/careShiftWindows";
import "./PatientBookingSteps.css";
import "./BookingFlowLayout.css";

/**
 * Patient picker for booking flows.
 * Auto-select is handled by parent when patients.length === 1.
 */
export default function PatientSelectStep({
  patients = [],
  selectedPatientId,
  onSelect,
  loading = false,
  subscription = null,
  searchShift = "",
  onContinue,
  onBack = null,
  continueLabel = "Continue",
  busy = false,
  hideHead = false,
  hideActions = false,
}) {
  const selected = patients.find(
    (p) =>
      String(p.patientId || p.patient_id || "") === String(selectedPatientId || "")
  );
      const passOkForShift = patientHasValidCarePassForShift(subscription, searchShift);
  const shift = resolveCanonicalSearchShift(searchShift);
  const passStatusLabel = (() => {
    if (!subscription) return null;
    if (passOkForShift) {
      if (subscription.careDaysRemaining != null) {
        return `Care Pass valid for this shift · ${subscription.careDaysRemaining} caregiver day${
          Number(subscription.careDaysRemaining) === 1 ? "" : "s"
        } left`;
      }
      return `Care Pass valid for this shift${
        subscription.daysRemaining != null ? ` · ${subscription.daysRemaining} day(s) left` : ""
      }`;
    }
    if (subscription.active) {
      return shift === "hourly"
        ? "Active pass does not cover hourly booking — an Hourly Pass is required"
        : "Active pass does not cover this shift — Short-term (≤1 month) or Quarterly (continuous >1 month) required";
    }
    return "No active Care Pass for this patient";
  })();

  return (
    <div className="pbs">
      {hideHead ? null : (
        <div className="pbs__head">
          <h2 className="pbs__title">Select patient</h2>
          <p className="pbs__sub">Bookings and subscriptions are tied to one patient at a time.</p>
        </div>
      )}

      {loading ? (
        <p className="pbs__muted">Loading patients…</p>
      ) : patients.length === 0 ? (
        <div className="pbs__empty">
          <p>No patients on this account yet.</p>
          <Link to="/elder-profile" className="pbs__link">
            Add a patient profile
          </Link>
        </div>
      ) : (
        <ul className="pbs__list" role="listbox" aria-label="Patients">
          {patients.map((p) => {
            const id = String(p.patientId || p.patient_id || "").trim();
            const active = id && id === String(selectedPatientId || "").trim();
            return (
              <li key={id || p.name}>
                <button
                  type="button"
                  className={`pbs__card${active ? " is-active" : ""}`}
                  onClick={() => id && onSelect?.(p)}
                  aria-selected={active}
                  role="option"
                  disabled={!id}
                >
                  <span className="pbs__card-radio" aria-hidden />
                  <span className="pbs__card-body">
                    <strong>{p.name || id}</strong>
                    <span>{id}</span>
                    {active ? <em>Selected</em> : null}
                    {p.isPrimary ? <em>Primary</em> : null}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {selected && passStatusLabel ? (
        <p className={`pbs__sub-status${passOkForShift ? " is-ok" : " is-warn"}`}>
          {passStatusLabel}
        </p>
      ) : null}

      {hideActions ? (
        <p className="pbs__manage-hint">
          <Link to="/elder-profile" className="pbs__link">
            Manage patients
          </Link>
        </p>
      ) : (
        <>
          <div className="pbs__actions">
            {onBack ? (
              <button type="button" className="pbs__link-btn" onClick={onBack} disabled={busy}>
                ← Back
              </button>
            ) : (
              <Link to="/elder-profile" className="pbs__link-btn">
                Manage patients
              </Link>
            )}
            <button
              type="button"
              className="pbs__primary"
              disabled={!selectedPatientId || busy || loading}
              onClick={() => onContinue?.()}
            >
              {busy ? "Checking…" : continueLabel}
            </button>
          </div>
          {onBack ? (
            <p className="pbs__manage-hint">
              <Link to="/elder-profile" className="pbs__link">
                Manage patients
              </Link>
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}

export function PoliceVerificationStep({
  required,
  onRequiredChange,
  onContinue,
  onBack,
  prvFeeInr = 500,
  reused = false,
  busy = false,
  showContinue = true,
  continueLabel = "Continue to Summary",
  compact = false,
  sidebar = null,
}) {
  const content = (
    <>
      <div className="pbs__head">
        <h2 className="pbs__title">Police Record Verification (Optional)</h2>
        <p className="bfl-prv__lead">
          Increase your peace of mind by verifying the selected caregiver through our legal verification partner.
        </p>
      </div>

      {!compact ? (
        <>
          <div className="bfl-prv__panels">
            <div className="bfl-prv__panel">
              <h4>What&apos;s included</h4>
              <ul>
                <li>
                  <span>Criminal Record Screening</span>
                  <strong>✓</strong>
                </li>
                <li>
                  <span>Police Database Check</span>
                  <strong>✓</strong>
                </li>
                <li>
                  <span>Identity Cross Verification</span>
                  <strong>✓</strong>
                </li>
                <li>
                  <span>Verification Report</span>
                  <strong>✓</strong>
                </li>
              </ul>
            </div>
            <div className="bfl-prv__panel">
              <h4>Logistics</h4>
              <ul>
                <li>
                  <span>Time Required</span>
                  <strong>24 – 72 Hours</strong>
                </li>
                <li>
                  <span>Fee</span>
                  <strong>₹{prvFeeInr} + 18% GST (One Time)</strong>
                </li>
              </ul>
            </div>
          </div>
          <p className="bfl-prv__notice">This verification does not delay caregiver deployment.</p>
        </>
      ) : null}

      <div className="bfl-prv__choices" role="radiogroup" aria-label="Require police verification">
        <label className={`bfl-prv__choice${required === true ? " is-active" : ""}`}>
          <input
            type="radio"
            name="prv-required"
            checked={required === true}
            onChange={() => onRequiredChange?.(true)}
          />
          <span>
            <strong>Yes, I want verification</strong>
            <small>
              {reused
                ? "Valid verification already on file — no new charge"
                : `Add ₹${prvFeeInr} + 18% GST Police Record Verification to this booking`}
            </small>
          </span>
        </label>
        <label className={`bfl-prv__choice${required === false ? " is-active" : ""}`}>
          <input
            type="radio"
            name="prv-required"
            checked={required === false}
            onChange={() => onRequiredChange?.(false)}
          />
          <span>
            <strong>No, I&apos;ll skip for now</strong>
            <small>You can purchase verification later from your dashboard</small>
          </span>
        </label>
      </div>

      {showContinue ? (
        <div className="bfl-prv__actions">
          {onBack ? (
            <button type="button" className="bfl-btn bfl-btn--ghost" onClick={onBack}>
              Back
            </button>
          ) : (
            <span />
          )}
          <button
            type="button"
            className="bfl-btn bfl-btn--primary"
            disabled={(required !== true && required !== false) || busy}
            onClick={onContinue}
          >
            {busy ? "Please wait…" : continueLabel}
          </button>
        </div>
      ) : null}
    </>
  );

  if (sidebar) {
    return <BookingFlowSplit sidebar={sidebar}>{content}</BookingFlowSplit>;
  }

  return <div className="pbs">{content}</div>;
}

export function BookingSummaryStep({
  caregiverName,
  caregiverFeeInr,
  visitFeeInr = null,
  bookingDays = 1,
  patientName,
  patientMeta,
  patientAddress = "",
  subscriptionLabel,
  subscriptionActive,
  subscriptionUntil,
  prvRequired,
  prvDeferred = false,
  prvFeeInr = 500,
  offerPoliceVerification = true,
  platformFeeInr = 0,
  gstInr = 0,
  caregiverChargeLabel = "Caregiver Charge",
  totalPayNowInr,
  caregiverFeeAfterService = true,
  confirmDetails = null,
  onBack,
  onConfirm,
  confirming = false,
  confirmLabel = "Confirm & send request",
}) {
  const days = Math.max(1, Number(bookingDays) || 1);
  const visitFee =
    visitFeeInr != null && Number.isFinite(Number(visitFeeInr))
      ? Math.round(Number(visitFeeInr))
      : days > 1 && caregiverFeeInr != null
        ? Math.round(Number(caregiverFeeInr) / days)
        : caregiverFeeInr;
  const packageFeeTotal =
    caregiverFeeInr != null
      ? Math.round(Number(caregiverFeeInr))
      : visitFee != null
        ? Math.round(Number(visitFee) * days)
        : null;
  const caregiverFeeLabel =
    packageFeeTotal == null
      ? null
      : days > 1
        ? `${formatInr(packageFeeTotal)} total (${formatInr(visitFee)}/day × ${days} days)`
        : `${formatInr(packageFeeTotal)} / visit`;

  const verificationLabel = !offerPoliceVerification
    ? "Not required (hourly)"
    : prvDeferred
      ? "After caregiver accepts"
      : prvRequired === true
        ? `Yes · ${formatInr(prvFeeInr)}`
        : prvRequired === false
          ? "No"
          : "Not selected";

  const sidebar = (
    <BookingFlowSidebar
      caregiverName={caregiverName}
      caregiverFeeLabel={caregiverFeeLabel}
      patientName={patientName}
      patientMeta={patientMeta}
      subscriptionLabel={subscriptionLabel}
      subscriptionActive={subscriptionActive}
      subscriptionUntil={subscriptionUntil}
      verificationLabel={verificationLabel}
      totalLabel={Number(totalPayNowInr) > 0 ? formatInr(totalPayNowInr) : "Pay after service"}
      totalHint={
        caregiverFeeAfterService
          ? days > 1 && packageFeeTotal != null
            ? `Caregiver fee ${formatInr(packageFeeTotal)} total · paid after service`
            : "Caregiver fee paid after service"
          : null
      }
    />
  );

  return (
    <BookingFlowSplit sidebar={sidebar}>
      <div>
        <div className="pbs__head" style={{ marginBottom: "0.85rem" }}>
          <h2 className="pbs__title">Confirm Details and Proceed</h2>
          <p className="pbs__sub">
            {offerPoliceVerification
              ? "Review shift terms, then confirm to send the acceptance request to the Care Attendant. Agreement and police verification follow after they accept."
              : "Review shift terms, then confirm to send the acceptance request to the Care Attendant. Agreement follows after they accept. Police verification is not required for hourly service."}
          </p>
        </div>

        {confirmDetails ? (
          <section className="bfl-summary__section bfl-summary__section--terms">
            <h3>{confirmDetails.heading || "Shift terms of care attendant"}</h3>
            <div className="bfl-summary__rows">
              {confirmDetails.billingLabel ? (
                <div className="bfl-summary__row">
                  <span>Billing terms</span>
                  <strong>{confirmDetails.billingLabel}</strong>
                </div>
              ) : null}
              {confirmDetails.rateLabel ? (
                <div className="bfl-summary__row">
                  <span>Rate</span>
                  <strong>{confirmDetails.rateLabel}</strong>
                </div>
              ) : null}
              {confirmDetails.shiftTiming ? (
                <div className="bfl-summary__row">
                  <span>Duty shift timing</span>
                  <strong>{confirmDetails.shiftTiming}</strong>
                </div>
              ) : null}
              {confirmDetails.startDate ? (
                <div className="bfl-summary__row">
                  <span>Start engagement date</span>
                  <strong>{confirmDetails.startDate}</strong>
                </div>
              ) : null}
              {confirmDetails.daysRequired ? (
                <div className="bfl-summary__row">
                  <span>No. of days duty required</span>
                  <strong>{confirmDetails.daysRequired}</strong>
                </div>
              ) : null}
            </div>
          </section>
        ) : null}

        <section className="bfl-summary__section">
          <h3>Booking details</h3>
          <div className="bfl-summary__rows">
            <div className="bfl-summary__row">
              <span>Caregiver</span>
              <strong>
                {caregiverName}
                {packageFeeTotal != null
                  ? days > 1
                    ? ` · ${formatInr(packageFeeTotal)} total`
                    : ` · ${formatInr(packageFeeTotal)}/visit`
                  : ""}
              </strong>
            </div>
            <div className="bfl-summary__row">
              <span>Patient</span>
              <strong>
                {patientName}
                {patientMeta ? ` · ${patientMeta}` : ""}
              </strong>
            </div>
            <div className="bfl-summary__row">
              <span>Subscription</span>
              <strong>
                {subscriptionLabel || "Service plan"}
                {subscriptionActive ? " · Active" : " · Inactive"}
              </strong>
            </div>
            <div className="bfl-summary__row">
              <span>Patient address</span>
              <strong>{patientAddress || "Address to be confirmed"}</strong>
            </div>
          </div>
        </section>

        <section className="bfl-summary__section">
          <h3>Payment summary</h3>
          <div className="bfl-summary__rows">
            <div className="bfl-summary__row">
              <span>{caregiverChargeLabel}</span>
              <strong>
                {packageFeeTotal != null ? formatInr(packageFeeTotal) : "—"}
                {days > 1 && visitFee != null
                  ? ` (${formatInr(visitFee)}/day × ${days} days)`
                  : ""}
                {caregiverFeeAfterService ? " (after service)" : ""}
              </strong>
            </div>
            {prvRequired && !prvDeferred ? (
              <div className="bfl-summary__row">
                <span>Police Verification Fee</span>
                <strong>{formatInr(prvFeeInr)}</strong>
              </div>
            ) : null}
            {platformFeeInr > 0 ? (
              <div className="bfl-summary__row">
                <span>Platform Fee</span>
                <strong>{formatInr(platformFeeInr)}</strong>
              </div>
            ) : null}
            {gstInr > 0 ? (
              <div className="bfl-summary__row">
                <span>GST 18%</span>
                <strong>{formatInr(gstInr)}</strong>
              </div>
            ) : null}
            <div className="bfl-summary__row bfl-summary__row--total">
              <span>Total pay now</span>
              <strong>
                {Number(totalPayNowInr) > 0 ? formatInr(totalPayNowInr) : "No fee now"}
              </strong>
            </div>
          </div>
        </section>

        <div className="bfl-prv__actions">
          {onBack ? (
            <button type="button" className="bfl-btn bfl-btn--ghost" onClick={onBack}>
              Back
            </button>
          ) : (
            <span />
          )}
          <button
            type="button"
            className="bfl-btn bfl-btn--primary"
            disabled={confirming}
            onClick={onConfirm}
          >
            {confirming ? "Processing…" : confirmLabel}
          </button>
        </div>
      </div>
    </BookingFlowSplit>
  );
}

export function AwaitingAcceptStep({
  bookingId,
  caregiverName,
  accepted = false,
  declined = false,
  polling = false,
  onProceed,
  onReselect,
  onDashboard,
}) {
  return (
    <div className="bfl-confirm">
      <div className={`bfl-confirm__check${accepted ? "" : " bfl-confirm__check--pending"}`} aria-hidden="true">
        {accepted ? "✓" : "…"}
      </div>
      <h2 className="bfl-confirm__title">
        {declined
          ? "Caregiver declined"
          : accepted
            ? "Caregiver accepted"
            : "Waiting for caregiver acceptance"}
      </h2>
      <p className="bfl-confirm__lede">
        {declined
          ? "This caregiver declined the request. Select another caregiver to continue."
          : accepted
            ? "You can proceed to the digital care agreement and police verification."
            : "Your care request was sent. Proceed stays disabled until the caregiver accepts."}
      </p>

      <div className="bfl-confirm__card">
        <div className="bfl-confirm__row">
          <span>Booking ID</span>
          <strong>{bookingId ? `NEMA-${String(bookingId).padStart(6, "0")}` : "—"}</strong>
        </div>
        <div className="bfl-confirm__row">
          <span>Caregiver</span>
          <strong>{caregiverName || "—"}</strong>
        </div>
        <div className="bfl-confirm__row">
          <span>Request status</span>
          <strong>
            <span className={`bfl-pill ${accepted ? "is-ok" : declined ? "is-warn" : "is-warn"}`}>
              {declined ? "Declined" : accepted ? "Accepted" : polling ? "Checking…" : "Awaiting acceptance"}
            </span>
          </strong>
        </div>
      </div>

      <div className="bfl-confirm__actions">
        {declined ? (
          <button type="button" className="bfl-btn bfl-btn--primary" onClick={onReselect}>
            Select another caregiver
          </button>
        ) : (
          <button
            type="button"
            className="bfl-btn bfl-btn--primary"
            disabled={!accepted}
            onClick={onProceed}
          >
            {accepted ? "Proceed to continue" : "Proceed to continue (waiting…)"}
          </button>
        )}
        {onDashboard ? (
          <button type="button" className="bfl-btn" onClick={onDashboard}>
            Go to Dashboard
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function BookingConfirmedStep({
  bookingId,
  caregiverName,
  patientName,
  dateLabel,
  timeLabel,
  subscriptionLabel,
  prvInitiated = false,
  offerPoliceVerification = true,
  awaitingCaregiverAcceptance = false,
  onDashboard,
  onViewBooking,
}) {
  return (
    <div className="bfl-confirm">
      <div className="bfl-confirm__check" aria-hidden="true">
        ✓
      </div>
      <h2 className="bfl-confirm__title">
        {awaitingCaregiverAcceptance ? "Caregiver Confirmation Pending" : "Booking Confirmed!"}
      </h2>
      <p className="bfl-confirm__lede">
        {awaitingCaregiverAcceptance
          ? "Your care request was sent to the caregiver. We’ll confirm the booking once they accept."
          : "Your booking is confirmed. The caregiver has accepted your care request."}
      </p>

      <div className="bfl-confirm__card">
        <div className="bfl-confirm__row">
          <span>Booking ID</span>
          <strong>{bookingId ? `NEMA-${String(bookingId).padStart(6, "0")}` : "—"}</strong>
        </div>
        <div className="bfl-confirm__row">
          <span>Caregiver</span>
          <strong>{caregiverName || "—"}</strong>
        </div>
        {awaitingCaregiverAcceptance ? (
          <div className="bfl-confirm__row">
            <span>Request status</span>
            <strong>
              <span className="bfl-pill is-warn">Awaiting caregiver acceptance</span>
            </strong>
          </div>
        ) : null}
        <div className="bfl-confirm__row">
          <span>Patient</span>
          <strong>{patientName || "—"}</strong>
        </div>
        {(dateLabel || timeLabel) && (
          <div className="bfl-confirm__row">
            <span>Date / Time</span>
            <strong>
              {[dateLabel, timeLabel].filter(Boolean).join(" · ")}
            </strong>
          </div>
        )}
        <div className="bfl-confirm__row">
          <span>Subscription</span>
          <strong>{subscriptionLabel || "Active plan"}</strong>
        </div>
        {offerPoliceVerification ? (
          <div className="bfl-confirm__row">
            <span>Police Verification</span>
            <strong>
              {prvInitiated ? (
                <span className="bfl-pill is-warn">Initiated</span>
              ) : (
                "Skipped"
              )}
            </strong>
          </div>
        ) : null}
        {offerPoliceVerification && prvInitiated ? (
          <div className="bfl-confirm__row">
            <span>Estimated completion</span>
            <strong>24 – 72 Hours</strong>
          </div>
        ) : null}
      </div>

      <p className="bfl-confirm__note">We have sent the details to your email and WhatsApp.</p>

      <div className="bfl-confirm__actions">
        <button type="button" className="bfl-btn bfl-btn--primary" onClick={onDashboard}>
          Go to Dashboard
        </button>
        <button type="button" className="bfl-btn" onClick={onViewBooking}>
          View Booking Details
        </button>
      </div>
    </div>
  );
}

export { BookingFlowSidebar, BookingFlowSplit, formatInr };
